import { Box, IconButton } from "@material-ui/core";
import { Close, Error, KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";
import { partition, uniq } from "lodash";
import { useEffect, useState } from "react";
import { LyfeTooltip, Typography, UploadStatus } from "..";
import DraftRounded from "../Icons/DraftRounded";
import FailedRounded from "../Icons/FailedRounded";
import SuccessfulRounded from "../Icons/SuccessfulRounded";
import CircularProgressWithLabel from "../Loading/CircularProgressWithLabel";
import { FlexContainerType, Maybe } from "../types/graphqlTypes";
import { distinctBy } from "../utils/distinct-by";
import uploaderStyles from "./uploader-styles";

export interface FormUploaderProps {
	type: FlexContainerType;
	status?: UploadStatus | null;
	error?: string;
	report?: FlexEntityUploadReport | null;
	progress?: number | null;
	t: (str: string, options?: any) => any;
	cancel: () => void;
	buttons?: Array<JSX.Element>;
}

enum AttributeValidationStatus {
	Invalid = "INVALID",
	Missing = "MISSING",
	Valid = "VALID",
}

export interface FlexEntityUploadReportBasicEntry {
	patientId: string;
	rowNumber?: number | null;
}

export interface FlexEntityUploadReport {
	submittedEntities: Array<FlexEntityUploadReportBasicEntry>;
	revokedEntities?: Array<FlexEntityUploadReportBasicEntry>;
	updatedEntities?: Maybe<Array<{ patientId: string; attributesUpdated: number; isSubmitted: boolean; rowNumber?: number | null }>>;
	draftedEntities?: Maybe<
		Array<{
			patientId: string;
			invalidAttributes: Array<{ name: string; status: AttributeValidationStatus }>;
			rowNumber?: number | null;
		}>
	>;
	failedToCreate: Array<{
		patientId: string;
		hasImpossibleHighQuantity?: boolean | null;
		invalidAttributes: Array<{ name: string; status: AttributeValidationStatus }>;
		rowNumber?: number | null;
	}>;
	failedToAdd?: Maybe<string>;
	failedToRevoke?: Array<FlexEntityUploadReportBasicEntry>;
	failedToUpdate?: Maybe<Array<FlexEntityUploadReportBasicEntry>>;
	ignoredRecords?: Maybe<string>;
	casesCreated: Array<string>;
	patientsCreated: Array<string>;
	createdOn: Date;
	completedIn: number;
	responseType: FlexContainerType;
	percentageSourceFile: number;
	isCaselessRecord?: Maybe<boolean>;
}

const Uploader: React.FC<FormUploaderProps> = ({ status, error, report, progress, t, cancel, buttons }) => {
	const [isOpen, setIsOpen] = useState(false);
	const { card, cardHeader, headerText, actionSection, reportLine, reportText } = uploaderStyles();
	const progressStates = [UploadStatus.Inspecting, UploadStatus.Uploading];
	const isProgressing = progressStates.includes(status as UploadStatus) && !error;
	const prefix = status === UploadStatus.PreliminaryReport ? "pre-" : "";
	const uploadFieldsProps = { reportLine, reportText, t, prefix, report };

	useEffect(() => {
		setIsOpen(!isProgressing);
	}, [isProgressing, status]);

	return (
		<Box className={card} flexDirection="column" justifyContent="space-between" width="100%">
			<Box display="flex" className={cardHeader} width="100%" justifyContent="space-between">
				<Box display="flex" className={`${cardHeader} ${headerText}`} width="60%">
					<Typography type="tableMainText" style={{ fontWeight: "bold" }}>
						{t(`${status}`)}
					</Typography>
				</Box>
				<Box display="flex" className={cardHeader} width="20%" px={2} alignItems="center" justifyContent="center">
					{isProgressing && progress && (
						<CircularProgressWithLabel
							color="secondary"
							thickness={5}
							size="30px"
							labelSize="8px"
							variant="determinate"
							value={progress}
						/>
					)}
					{error && (
						<LyfeTooltip title={error}>
							<Error />
						</LyfeTooltip>
					)}
					{!isProgressing && (
						<>
							<IconButton size="small" onClick={() => setIsOpen(!isOpen)} style={{ color: "white" }}>
								{!isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
							</IconButton>
							<IconButton size="small" onClick={cancel} style={{ color: "white", marginRight: 12 }} id="close-uploader">
								<Close />
							</IconButton>
						</>
					)}
				</Box>
			</Box>
			{isOpen && (
				<>
					{report?.responseType === FlexContainerType.Form && <FormUploadFields {...uploadFieldsProps} />}
					{report?.responseType === FlexContainerType.Record && <RecordUploadFields {...uploadFieldsProps} />}
					<Box
						className={actionSection}
						width="100%"
						display="flex"
						justifyContent={buttons?.length === 1 ? "end" : "space-between"}
						alignItems="center"
						px={3}
					>
						{buttons && buttons.map((btn) => <Box>{btn}</Box>)}
					</Box>
				</>
			)}
		</Box>
	);
};

interface FormUploadFieldsProps {
	reportLine: string;
	reportText: string;
	t: (str: string, options?: any) => any;
	prefix: string;
	report: FlexEntityUploadReport | undefined | null;
}

const FormUploadFields: React.FC<FormUploadFieldsProps> = ({ reportLine, reportText, t, prefix, report }) => {
	const submittedAmount = distinctBy(report?.submittedEntities || [], (entity) => entity.patientId).length;
	const updatedAmount = report?.updatedEntities?.filter((entity) => !entity.isSubmitted).length;
	const updatedAndSubmittedAmount = report?.updatedEntities?.filter((entity) => entity.isSubmitted).length;
	const draftedAmount = uniq(report?.draftedEntities?.map((p) => p.patientId) || []).length;
	const failedToCreateAmount = uniq(report?.failedToCreate?.map((p) => p.patientId) || []).length;
	const failedToUpdateAmount = uniq(report?.failedToUpdate?.map((p) => p.patientId) || []).length;
	const caseCreatedAmount = report?.casesCreated.length;
	const patientCreatedAmount = report?.patientsCreated.length;

	return (
		<Box style={{ backgroundColor: "white" }}>
			{!!submittedAmount && (
				<Box display="flex" className={reportLine}>
					<SuccessfulRounded />{" "}
					<Typography type="tableMainText" className={reportText}>
						{t(`${prefix}upload-forms-submitted`, { amount: submittedAmount })}
					</Typography>
				</Box>
			)}
			{!!updatedAmount && (
				<Box display="flex" className={reportLine}>
					<SuccessfulRounded />{" "}
					<Typography type="tableMainText" className={reportText}>
						{t(`${prefix}upload-forms-updated`, {
							amount: updatedAmount,
						})}
					</Typography>
				</Box>
			)}
			{!!updatedAndSubmittedAmount && (
				<Box display="flex" className={reportLine}>
					<SuccessfulRounded />{" "}
					<Typography type="tableMainText" className={reportText}>
						{t(`${prefix}upload-forms-updated-and-submitted`, {
							amount: updatedAndSubmittedAmount,
						})}
					</Typography>
				</Box>
			)}
			{!!draftedAmount && (
				<Box display="flex" className={reportLine}>
					<DraftRounded />{" "}
					<Typography type="tableMainText" className={reportText}>
						{t(`${prefix}upload-forms-drafted`, { amount: draftedAmount })}
					</Typography>
				</Box>
			)}
			{!!failedToCreateAmount && (
				<Box display="flex" className={reportLine}>
					<FailedRounded />{" "}
					<Typography type="tableMainText" className={reportText}>
						{t(`${prefix}upload-forms-creation-failed`, { amount: failedToCreateAmount })}
					</Typography>
				</Box>
			)}
			{!!failedToUpdateAmount && (
				<Box display="flex" className={reportLine}>
					<FailedRounded />{" "}
					<Typography type="tableMainText" className={reportText}>
						{t(`${prefix}upload-forms-update-failed`, { amount: failedToUpdateAmount })}
					</Typography>
				</Box>
			)}
			{!!caseCreatedAmount && (
				<Box display="flex" className={reportLine} pb={2}>
					<SuccessfulRounded />{" "}
					<Typography type="tableMainText" className={reportText}>
						{t(`${prefix}upload-forms-cases-created`, { noCases: caseCreatedAmount, amount: patientCreatedAmount })}
					</Typography>
				</Box>
			)}
		</Box>
	);
};

interface RecordUploadFieldsProps {
	reportLine: string;
	reportText: string;
	t: (str: string, options?: any) => any;
	prefix: string;
	report: FlexEntityUploadReport | undefined | null;
}

const RecordUploadFields: React.FC<RecordUploadFieldsProps> = ({ reportLine, reportText, t, prefix, report }) => {
	const distinctPatientAmount = Array.from(new Set(report?.submittedEntities.map((e) => e.patientId))).filter(Boolean).length || 0;
	const [failedToCreateByQuantity, failedToCreateOtherReasons] = partition(
		report?.failedToCreate ?? [],
		(e) => e.hasImpossibleHighQuantity
	);
	const failedToCreateAmount = failedToCreateOtherReasons.length;
	const caseUpdatedAmount = report?.casesCreated.length;
	const processedSourceFileAmount = report?.percentageSourceFile || 0;
	const submittedAmount = report?.submittedEntities.length || 0;
	const revokedCount = report?.revokedEntities?.length;
	const revokePatientsCount = uniq(report?.revokedEntities?.map((e) => e.patientId) || []).length;
	const failedToRevokeCount = report?.failedToRevoke?.length;
	const failedToRevokePatientsCount = uniq(report?.failedToRevoke?.map((e) => e.patientId) || []).length;
	const isPatientOrPurchase = report?.isCaselessRecord ? t("product") : t("patient");

	const failedToAddRecordsCount = report?.failedToAdd ? JSON.parse(report?.failedToAdd).length : 0;
	const ignoredRecordsCount = report?.ignoredRecords ? JSON.parse(report.ignoredRecords).length : 0;

	return (
		<Box style={{ backgroundColor: "white", paddingBottom: 12 }}>
			{!!distinctPatientAmount && (
				<Box display="flex" className={reportLine}>
					<SuccessfulRounded />{" "}
					<Typography type="tableMainText" className={reportText}>
						{t(`${prefix}upload-records-submitted`, { amount: distinctPatientAmount, isPatientOrPurchase })}
					</Typography>
				</Box>
			)}
			{!!revokedCount && (
				<Box display="flex" className={reportLine}>
					<SuccessfulRounded />{" "}
					<Typography type="tableMainText" className={reportText}>
						{t(`${prefix}upload-records-revoked`, {
							amount: revokedCount,
							patientsCount: revokePatientsCount,
							isPatientOrPurchase,
						})}
					</Typography>
				</Box>
			)}
			{!!failedToCreateAmount && (
				<Box display="flex" className={reportLine}>
					<FailedRounded />{" "}
					<Typography type="tableMainText" className={reportText}>
						{t(`${prefix}upload-records-creation-failed`, { amount: failedToCreateAmount, isPatientOrPurchase })}
					</Typography>
				</Box>
			)}
			{!!failedToRevokeCount && (
				<Box display="flex" className={reportLine}>
					<FailedRounded />{" "}
					<Typography type="tableMainText" className={reportText}>
						{t(`${prefix}upload-records-revocation-failed`, {
							amount: failedToRevokeCount,
							patientsCount: failedToRevokePatientsCount,
							isPatientOrPurchase,
						})}
					</Typography>
				</Box>
			)}
			{!!failedToCreateByQuantity.length && (
				<Box display="flex" className={reportLine}>
					<FailedRounded />{" "}
					<Typography type="tableMainText" className={reportText}>
						{t(`${prefix}record-upload-error-due-to-quantity`, {
							amount: failedToCreateByQuantity.length,
						})}
					</Typography>
				</Box>
			)}
			{!!caseUpdatedAmount && (
				<Box display="flex" className={reportLine}>
					<SuccessfulRounded />{" "}
					<Typography type="tableMainText" className={reportText}>
						{t(`${prefix}upload-records-cases-created`, { amount: caseUpdatedAmount, isPatientOrPurchase })}
					</Typography>
				</Box>
			)}
			{!!(processedSourceFileAmount || submittedAmount) && (
				<Box display="flex" className={reportLine}>
					<SuccessfulRounded />{" "}
					<Typography type="tableMainText" className={reportText}>
						{t(`${prefix}percent-source-file`, {
							amount: processedSourceFileAmount,
							recordCount: submittedAmount,
						})}
					</Typography>
				</Box>
			)}
			{!!failedToAddRecordsCount && (
				<Box display="flex" className={reportLine}>
					<Box minWidth={"15px"}>
						<FailedRounded />{" "}
					</Box>
					<Typography type="tableMainText" className={reportText}>
						{t(`${prefix}existing-records-count`, { amount: failedToAddRecordsCount })}
					</Typography>
				</Box>
			)}
			{!!ignoredRecordsCount && (
				<Box display="flex" className={reportLine} pb={2}>
					<Box minWidth={"15px"}>
						<FailedRounded />{" "}
					</Box>
					<Typography type="tableMainText" className={reportText}>
						{t(`${prefix}existing-file-duplicates-count`, { amount: ignoredRecordsCount })}
					</Typography>
				</Box>
			)}{" "}
		</Box>
	);
};

export default Uploader;
